import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

export interface PlatformStats {
  totalUsers: number;
  activeTables: number;
  totalPKCashInCirculation: number;
  totalPlayCoinsInCirculation: number;
  onlinePlayers: number;
  tablesPlaying: number;
  tablesWaiting: number;
}

class StatsService {
  private cachedStats: PlatformStats | null = null;
  private lastFetchTime: number = 0;
  private cacheDurationMs: number = 60000; // Cache for 1 minute

  /**
   * GET PLATFORM STATS
   * Fetches real statistics from Firestore
   */
  async getPlatformStats(forceRefresh: boolean = false): Promise<PlatformStats> {
    const now = Date.now();
    
    // Return cached stats if still valid
    if (!forceRefresh && this.cachedStats && (now - this.lastFetchTime) < this.cacheDurationMs) {
      return this.cachedStats;
    }

    try {
      console.log('📊 [StatsService] Fetching platform stats...');

      // Fetch all stats in parallel
      const [
        totalUsers,
        tableStats,
        walletStats,
        onlinePlayers,
      ] = await Promise.all([
        this.getTotalUsers(),
        this.getTableStats(),
        this.getWalletStats(),
        this.getOnlinePlayersCount(),
      ]);

      this.cachedStats = {
        totalUsers,
        activeTables: tableStats.playing + tableStats.waiting,
        totalPKCashInCirculation: walletStats.totalPKCash,
        totalPlayCoinsInCirculation: walletStats.totalPlayCoins,
        onlinePlayers,
        tablesPlaying: tableStats.playing,
        tablesWaiting: tableStats.waiting,
      };

      this.lastFetchTime = now;
      console.log('✅ [StatsService] Stats fetched:', this.cachedStats);

      return this.cachedStats;
    } catch (error) {
      console.error('❌ [StatsService] Error fetching stats:', error);
      
      // Return default stats on error
      return {
        totalUsers: 0,
        activeTables: 0,
        totalPKCashInCirculation: 0,
        totalPlayCoinsInCirculation: 0,
        onlinePlayers: 0,
        tablesPlaying: 0,
        tablesWaiting: 0,
      };
    }
  }

  /**
   * GET TOTAL USERS COUNT
   */
  private async getTotalUsers(): Promise<number> {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getCountFromServer(usersRef);
      return snapshot.data().count;
    } catch (error) {
      console.error('Error getting total users:', error);
      return 0;
    }
  }

  /**
   * GET TABLE STATS
   * Returns count of tables by status
   */
  private async getTableStats(): Promise<{ playing: number; waiting: number; total: number }> {
    try {
      const tablesRef = collection(db, 'tables');
      
      // Get playing tables
      const playingQuery = query(tablesRef, where('status', '==', 'playing'));
      const playingSnapshot = await getCountFromServer(playingQuery);
      const playing = playingSnapshot.data().count;

      // Get waiting tables
      const waitingQuery = query(tablesRef, where('status', '==', 'waiting'));
      const waitingSnapshot = await getCountFromServer(waitingQuery);
      const waiting = waitingSnapshot.data().count;

      return {
        playing,
        waiting,
        total: playing + waiting,
      };
    } catch (error) {
      console.error('Error getting table stats:', error);
      return { playing: 0, waiting: 0, total: 0 };
    }
  }

  /**
   * GET WALLET STATS
   * Returns total PKCash and PlayCoins in circulation
   */
  private async getWalletStats(): Promise<{ totalPKCash: number; totalPlayCoins: number }> {
    try {
      const walletsRef = collection(db, 'wallets');
      const snapshot = await getDocs(walletsRef);

      let totalPKCash = 0;
      let totalPlayCoins = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        totalPKCash += data.pkCash || 0;
        totalPlayCoins += data.playCoins || 0;
      });

      return { totalPKCash, totalPlayCoins };
    } catch (error) {
      console.error('Error getting wallet stats:', error);
      return { totalPKCash: 0, totalPlayCoins: 0 };
    }
  }

  /**
   * GET ONLINE PLAYERS COUNT
   * Uses presence system if available, otherwise estimates from active tables
   */
  private async getOnlinePlayersCount(): Promise<number> {
    try {
      // Option 1: Check presence collection if you have one
      const presenceRef = collection(db, 'presence');
      const onlineQuery = query(presenceRef, where('online', '==', true));
      
      try {
        const presenceSnapshot = await getCountFromServer(onlineQuery);
        const count = presenceSnapshot.data().count;
        if (count > 0) {
          return count;
        }
      } catch {
        // Presence collection might not exist, fall through to alternative
      }

      // Option 2: Count unique players at active tables
      const tablesRef = collection(db, 'tables');
      const activeQuery = query(tablesRef, where('status', 'in', ['playing', 'waiting']));
      const tablesSnapshot = await getDocs(activeQuery);

      const uniquePlayers = new Set<string>();
      tablesSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.players && Array.isArray(data.players)) {
          data.players.forEach((playerId: string) => {
            if (playerId) uniquePlayers.add(playerId);
          });
        }
      });

      return uniquePlayers.size;
    } catch (error) {
      console.error('Error getting online players:', error);
      return 0;
    }
  }

  /**
   * SUBSCRIBE TO STATS (Real-time updates)
   * Useful for live dashboard updates
   */
  subscribeToStats(
    onUpdate: (stats: PlatformStats) => void,
    refreshIntervalMs: number = 30000
  ): Unsubscribe {
    // Initial fetch
    this.getPlatformStats(true).then(onUpdate);

    // Set up interval for periodic refreshes
    const intervalId = setInterval(async () => {
      const stats = await this.getPlatformStats(true);
      onUpdate(stats);
    }, refreshIntervalMs);

    // Return unsubscribe function
    return () => {
      clearInterval(intervalId);
    };
  }

  /**
   * GET QUICK STATS (lighter weight)
   * Only fetches table and player counts, not wallet totals
   */
  async getQuickStats(): Promise<{
    activeTables: number;
    onlinePlayers: number;
  }> {
    try {
      const [tableStats, onlinePlayers] = await Promise.all([
        this.getTableStats(),
        this.getOnlinePlayersCount(),
      ]);

      return {
        activeTables: tableStats.total,
        onlinePlayers,
      };
    } catch (error) {
      console.error('Error getting quick stats:', error);
      return { activeTables: 0, onlinePlayers: 0 };
    }
  }

  /**
   * CLEAR CACHE
   */
  clearCache(): void {
    this.cachedStats = null;
    this.lastFetchTime = 0;
    console.log('🧹 Stats cache cleared');
  }
}

export const statsService = new StatsService();
export default statsService;